// Bannière in-app qui surface les invitations en attente envoyées à
// l'email du user authentifié, tous accounts confondus. Affiche le nom
// de l'org + le rôle proposé pour chaque pending, avec un bouton
// « Accepter » qui accepte DIRECTEMENT in-app (cf useAcceptInvitation →
// invitations.acceptMyPending). Plus besoin du lien email : ça débloque
// le cas d'un user déjà connecté avec sa propre org (le lien AuthKit ne
// surface rien pour lui).
//
// Utilisée par :
//   - AgencyStep (au-dessus du wizard) : prévient un user qui crée son
//     org qu'il a aussi des invitations pending sur la même adresse.
//   - CockpitShell TitleBar : informe les users déjà actifs qu'une
//     nouvelle invitation est arrivée (l'email peut être manqué).
//
// La cloche NotificationsTray (SideRail gauche) est la maison permanente
// de la même liste — les deux partagent useAcceptInvitation.

import { useQuery } from "convex/react";
import { Mail, X } from "@/shared/ui/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import { Button, Carte, Infobulle, Ligne, Lignes, Section } from "@/shared/ui";
import { useAcceptInvitation } from "./useAcceptInvitation";

export function PendingInvitationsBanner({
  variant = "card",
}: {
  /**
   * "card" — bloc large pour l'onboarding (au-dessus du form agency).
   * "compact" — bandeau fin pour le top du cockpit shell.
   */
  variant?: "card" | "compact";
}) {
  const { t } = useTranslation();
  const pendings = useQuery(api.invitations.listMyPending, {});
  const [dismissed, setDismissed] = useState(false);
  const { acceptInvitation, acceptingId } = useAcceptInvitation();

  if (!pendings || pendings.length === 0 || dismissed) return null;

  if (variant === "compact") {
    // Cas le plus courant = 1 invitation → bouton accept inline. Pour
    // plusieurs, on garde le bandeau léger (le compte) et on laisse la
    // cloche NotificationsTray gérer le détail un-par-un.
    const single = pendings.length === 1 ? pendings[0]! : null;
    return (
      <div className="px-3 py-2">
        <Carte remplissage="sm" className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2.5 text-fg">
            <Mail className="h-4 w-4 shrink-0 text-muted" />
            <span className="truncate text-[13px]">
              {single
                ? t("invitations.banner.compactOne", {
                    account: single.accountName ?? "—",
                    role: single.roleName ?? "—",
                  })
                : t("invitations.banner.compactMany", { count: pendings.length })}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {single && (
              <Button
                variant="accent"
                size="md"
                onClick={() => void acceptInvitation(single._id, single.accountName)}
                disabled={acceptingId !== null}
              >
                {acceptingId === single._id
                  ? t("invitations.banner.accepting")
                  : t("invitations.banner.accept")}
              </Button>
            )}
            <Infobulle texte={t("common.dismiss")}>
              <Button
                variant="ghost"
                size="md"
                iconOnly
                onClick={() => setDismissed(true)}
                aria-label={t("common.dismiss")}
              >
                <X className="h-4 w-4" />
              </Button>
            </Infobulle>
          </div>
        </Carte>
      </div>
    );
  }

  return (
    <Section
      className="mb-5"
      icone={<Mail />}
      titre={t("invitations.banner.title", { count: pendings.length })}
      remplissage="aucun"
    >
      <Lignes>
        {pendings.map((p) => (
          <Ligne
            key={p._id}
            etiquette={p.accountName ?? "—"}
            sous={p.roleName || undefined}
            apres={
              <Button
                variant="accent"
                size="md"
                onClick={() => void acceptInvitation(p._id, p.accountName)}
                disabled={acceptingId !== null}
              >
                {acceptingId === p._id
                  ? t("invitations.banner.accepting")
                  : t("invitations.banner.accept")}
              </Button>
            }
          />
        ))}
      </Lignes>
    </Section>
  );
}
